import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

import { USER_INFO_QUERY_KEYS } from './keys';

import * as meService from 'services/me';
import * as noticeService from 'services/notices';
import * as reminderService from 'services/reminder';

import { getObjectFromArray } from 'utils/array';

import { handleError } from 'utils/errorHandler';

import type { Notice } from 'interface/notices';
import type { Reminder } from 'interface/reminders';

export interface NoticeItem extends Notice {
  id: number;
}

export interface ReminderItem extends Reminder {
  id: number;
}

export interface UserInfo {
  id?: number;
  country?: string;
  [key: string]: unknown;
}

export type UserPermissionMap = Record<string, string | number | boolean | undefined>;

export interface UserInfoData {
  permissions: UserPermissionMap;
  user: UserInfo;
  reminders: ReminderItem[];
  notices: NoticeItem[];
}

interface UserInfoResponse {
  permissions?: {
    permissions?: Array<string | number>;
  };
  user?: UserInfo;
  notices?: NoticeItem[];
  reminders?: ReminderItem[];
}

type UserInfoQueryOptions = Omit<UseQueryOptions<UserInfoResponse, Error, UserInfoData>, 'queryKey' | 'queryFn'>;

type NoticeUpdatePayload = {
  id: number;
  notice: Notice;
};

type ReminderUpdatePayload = {
  id: number;
  reminder: Reminder;
};

const EMPTY_USER_INFO: UserInfoData = {
  permissions: {},
  user: {},
  notices: [],
  reminders: [],
};

const transformUserInfo = (raw?: UserInfoResponse): UserInfoData => {
  return {
    permissions: raw?.permissions?.permissions ? getObjectFromArray(raw.permissions.permissions) : {},
    user: raw?.user || {},
    notices: raw?.notices || [],
    reminders: raw?.reminders || [],
  };
};

export const useUserInfoQuery = (options?: UserInfoQueryOptions): UseQueryResult<UserInfoData, Error> => {
  const maintenanceFlag = import.meta.env.REACT_APP_MAINTENANCE_MODE ?? process.env.REACT_APP_MAINTENANCE_MODE ?? '0';
  const isMaintenanceMode = Boolean(Number(maintenanceFlag));
  const enabled = options?.enabled ?? !isMaintenanceMode;

  return useQuery<UserInfoResponse, Error, UserInfoData>({
    queryKey: [USER_INFO_QUERY_KEYS.USER_INFO],
    queryFn: () => meService.fetchInfo<UserInfoResponse>(),
    select: transformUserInfo,
    refetchOnMount: false,
    ...options,
    enabled,
  });
};

export const useUserInfo = (): UserInfoData => {
  return useUserInfoQuery().data || EMPTY_USER_INFO;
};

export const useInformationState = () => {
  const query = useUserInfoQuery();

  return {
    value: query.data || EMPTY_USER_INFO,
    loading: query.isLoading,
  };
};

export const useCurrentUser = (): UserInfo => {
  return useUserInfo().user;
};

export const useUserPermissions = (): UserInfoData['permissions'] => {
  return useUserInfo().permissions;
};

export const useUserNotices = (): NoticeItem[] => {
  return useUserInfo().notices;
};

export const useUserReminders = (): ReminderItem[] => {
  return useUserInfo().reminders;
};

const useInvalidateUserInfo = () => {
  const queryClient = useQueryClient();

  return () => queryClient.invalidateQueries({ queryKey: [USER_INFO_QUERY_KEYS.USER_INFO] });
};

const handleMutationError = (error: object) => {
  handleError(error);
};

export const useCreateNoticeMutation = () => {
  const invalidateUserInfo = useInvalidateUserInfo();

  return useMutation({
    mutationFn: (notice: Notice) => noticeService.create(notice),
    onSuccess: invalidateUserInfo,
    onError: handleMutationError,
  });
};

export const useUpdateNoticeMutation = () => {
  const invalidateUserInfo = useInvalidateUserInfo();

  return useMutation({
    mutationFn: ({ id, notice }: NoticeUpdatePayload) => noticeService.update(id, notice),
    onSuccess: invalidateUserInfo,
    onError: handleMutationError,
  });
};

export const useDeleteNoticeMutation = () => {
  const invalidateUserInfo = useInvalidateUserInfo();

  return useMutation({
    mutationFn: (id: number) => noticeService.remove(id),
    onSuccess: invalidateUserInfo,
    onError: handleMutationError,
  });
};

export const useCreateReminderMutation = () => {
  const invalidateUserInfo = useInvalidateUserInfo();

  return useMutation({
    mutationFn: (reminder: Reminder) => reminderService.create(reminder),
    onSuccess: invalidateUserInfo,
    onError: handleMutationError,
  });
};

export const useUpdateReminderMutation = () => {
  const invalidateUserInfo = useInvalidateUserInfo();

  return useMutation({
    mutationFn: ({ id, reminder }: ReminderUpdatePayload) => reminderService.updateReminder(id, reminder),
    onSuccess: invalidateUserInfo,
    onError: handleMutationError,
  });
};

export const useDeleteReminderMutation = () => {
  const invalidateUserInfo = useInvalidateUserInfo();

  return useMutation({
    mutationFn: (id: number) => reminderService.remove(id),
    onSuccess: invalidateUserInfo,
    onError: handleMutationError,
  });
};
